import { useRef, type Dispatch, type SetStateAction } from "react";
import type { VertexTarget } from "../model/inp/editNetwork.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { StoredProject } from "../model/storage.ts";
import type { useUndoRedo } from "./useUndoRedo.ts";

/**
 * Every raw-`rawSections` edit funnels through one of these three:
 * `mutateRaw` re-stamps `lastModified` and clears any stale simulation
 * results; `mutateRawUndoable` additionally pushes the pre-edit snapshot
 * onto `undoRedo` for one atomic action (create/delete/move/import...);
 * `mutateRawCoalesced` defers that push until the live-typing field that
 * triggered it loses focus (`commitPendingCoalescedUndo`), so a burst of
 * keystrokes becomes one undo entry instead of one per keystroke.
 */
export function useRawSectionEditor(
  loaded: StoredProject | null,
  setLoaded: Dispatch<SetStateAction<StoredProject | null>>,
  undoRedo: ReturnType<typeof useUndoRedo>,
  clearSimulationResults: () => void,
  setSelected: (ref: ElementRef | null) => void,
  setMultiSelected: (refs: ElementRef[]) => void,
  setFocusedVertex: (value: { target: VertexTarget; index: number } | null) => void
) {
  // A plain ref (not state): changing it must never trigger a re-render,
  // only `commitPendingCoalescedUndo` reads/clears it in response to a real
  // DOM event (blur, selection change).
  const pendingCoalesceRef = useRef<ReadonlyMap<string, string[][]> | null>(null);

  /** Every edit (property change, move, add, delete) goes through this — one place that re-stamps `lastModified` and leaves everything else about `loaded` untouched. */
  function mutateRaw(
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) {
    setLoaded((current) =>
      current
        ? { ...current, rawSections: fn(current.rawSections), lastModified: Date.now() }
        : current
    );
    clearSimulationResults();
  }

  /**
   * Wraps one section's `writeXxx(raw, data)` writer into a shared
   * `onChange`-shaped handler for its editor panel. Goes through
   * `mutateRawCoalesced`, not plain `mutateRaw` — these editors hand back
   * their whole section array on every call, whether triggered by
   * live-typing or a discrete add/remove-row button, with no way to tell
   * which. Accepted trade-off: two add/remove-row clicks on the SAME button
   * with no focus change between them also coalesce into one undo entry —
   * still strictly better than no undo at all.
   */
  function mutateSection<T>(
    writer: (raw: ReadonlyMap<string, string[][]>, data: T) => ReadonlyMap<string, string[][]>
  ) {
    return (data: T) => mutateRawCoalesced((raw) => writer(raw, data));
  }

  /**
   * Same as `mutateRaw`, but also pushes the PRE-edit `rawSections` onto the
   * undo stack and clears any pending redo — for every edit that's a single
   * atomic action (create/delete an element, edit geometry, import a GIS
   * layer, save from a dialog's "Save" button). Live-typing fields go
   * through `mutateRawCoalesced` instead — pushing a full undo entry per
   * KEYSTROKE would make undo useless for them.
   */
  function mutateRawUndoable(
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) {
    if (!loaded) return;
    // A live-typing edit still "in progress" (its commit deferred to a
    // blur that hasn't happened yet) must land as its OWN undo entry
    // before this one, or it would get folded into whatever this action's
    // undo reverts. A real blur almost always precedes this anyway; this
    // is the defensive fallback for the rare case that doesn't.
    commitPendingCoalescedUndo();
    undoRedo.pushUndo(loaded.rawSections);
    mutateRaw(fn);
  }

  /**
   * For fields whose `onChange` fires per KEYSTROKE rather than as one
   * atomic action — pushing a full undo entry per keystroke would make undo
   * useless for them. The PRE-edit snapshot is captured once, on the FIRST
   * call of a live "edit session" (`pendingCoalesceRef.current` still
   * `null`); every later keystroke in that session just calls plain
   * `mutateRaw`. The undo-stack push itself is deferred to
   * `commitPendingCoalescedUndo`, fired once the field loses focus — one
   * undo entry per field edited, like a real text editor groups a burst of
   * typing into one step.
   */
  function mutateRawCoalesced(
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) {
    if (!loaded) return;
    if (pendingCoalesceRef.current === null) pendingCoalesceRef.current = loaded.rawSections;
    mutateRaw(fn);
  }

  /** Pushes whatever `mutateRawCoalesced` snapshot is pending (a no-op if there isn't one) — called on `PropertyPanel`'s own `onBlur` and defensively from `mutateRawUndoable`/`undo`/`redo` so an in-progress edit is never silently folded into an unrelated undo entry. */
  function commitPendingCoalescedUndo() {
    if (pendingCoalesceRef.current !== null) {
      undoRedo.pushUndo(pendingCoalesceRef.current);
      pendingCoalesceRef.current = null;
    }
  }

  /** Discards (rather than commits) whatever `mutateRawCoalesced` snapshot is pending — for `resetProjectSessionState`, since no undo stack survives a project switch anyway. */
  function resetPendingCoalesce() {
    pendingCoalesceRef.current = null;
  }

  /** Applies `rawSections` plus the 4 side effects every undo/redo shares — factored out since `undo()` now has two ways to arrive at a snapshot to restore. */
  function restore(rawSections: ReadonlyMap<string, string[][]>) {
    setLoaded((current) =>
      current ? { ...current, rawSections, lastModified: Date.now() } : current
    );
    clearSimulationResults();
    setSelected(null);
    setMultiSelected([]);
    setFocusedVertex(null);
  }

  function undo() {
    if (!loaded) return;
    // A pending coalesced edit can't go through `commitPendingCoalescedUndo()`
    // then `undoRedo.undo()` here — pushing and popping the SAME entry in one
    // call would need `undo()`'s own synchronous read to see a `pushUndo()`
    // called moments earlier in that same call, which React's state updates
    // don't guarantee (found live: undoStack still read as empty right after
    // pushing to it, so the very first Ctrl+Z mid-typing silently no-opped).
    // Committing-then-popping the same entry always nets out to "undoStack
    // unchanged, redoStack becomes exactly the current value" — compute that
    // directly instead.
    if (pendingCoalesceRef.current !== null) {
      const previous = pendingCoalesceRef.current;
      pendingCoalesceRef.current = null;
      undoRedo.replaceRedoStack(loaded.rawSections);
      restore(previous);
      return;
    }
    const previous = undoRedo.undo(loaded.rawSections);
    if (previous === undefined) return;
    restore(previous);
  }

  function redo() {
    if (!loaded) return;
    // Same hazard as `undo()` above, but simpler to resolve: a pending edit
    // IS a real new action, and any real edit invalidates whatever redo
    // stack existed — committing it is the whole job, there's nothing left
    // to redo afterward.
    if (pendingCoalesceRef.current !== null) {
      commitPendingCoalescedUndo();
      return;
    }
    const next = undoRedo.redo(loaded.rawSections);
    if (next === undefined) return;
    restore(next);
  }

  return {
    mutateSection,
    mutateRawUndoable,
    mutateRawCoalesced,
    commitPendingCoalescedUndo,
    resetPendingCoalesce,
    undo,
    redo,
  };
}
